export interface AttributionData {
    utm_source: string;
    utm_medium: string;
    utm_campaign: string;
    utm_content: string;
    utm_term: string;
    landing_page: string;
    referrer: string;
    timestamp: string;
}

// 1. Check Utm Data cookie coming from free-now.com
const getAttributionFromFreenowDomain = (): Partial<AttributionData> | null => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(^| )utm_data=([^;]+)'));
    if (match) {
        try {
            return JSON.parse(decodeURIComponent(match[2]));
        } catch (e) {
            console.error('Failed to parse Freenow utm_data cookie', e);
            return null;
        }
    }
    return null;
};

// 2. Cookie Helpers
const setAttributionCookie = (name: 'ft_attr' | 'lt_attr', data: AttributionData) => {
    const days = 180;
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);

    // Environment Isolation
    const hostname = window.location.hostname;
    let targetDomain = '';
    if (hostname.includes('prelive.free-now.com')) {
        targetDomain = '.prelive.free-now.com';
    } else if (hostname.includes('free-now.com')) {
        targetDomain = '.free-now.com';
    }

    const domainStr = targetDomain ? `domain=${targetDomain};` : '';
    document.cookie = `${name}=${encodeURIComponent(JSON.stringify(data))};expires=${date.toUTCString()};${domainStr}path=/;Secure;SameSite=Lax`;
};

export const getAttributionCookie = (name: 'ft_attr' | 'lt_attr'): AttributionData | null => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if (match) {
        try {
            return JSON.parse(decodeURIComponent(match[2]));
        } catch (e) {
            return null;
        }
    }
    return null;
};

// 3. Internal Referrer Check
const isInternalReferrer = (referrerStr: string): boolean => {
    if (!referrerStr) return false;
    try {
        const hostname = new URL(referrerStr).hostname;
        return hostname === 'free-now.com' || hostname.endsWith('.free-now.com');
    } catch {
        return false;
    }
};

// 4. URL inspection
export const processAttribution = () => {
    const params = new URLSearchParams(window.location.search);
    const utmData = getAttributionFromFreenowDomain(); // Ken's cookie
    const referrer = document.referrer || '';

    // Initialize with empty strings
    let source = params.get('utm_source') || '';
    let medium = params.get('utm_medium') || '';

    // 1. If no UTMs in URL, try Click IDs
    if (!source) {
        if (params.has('gclid')) {
            source = 'google';
            medium = 'cpc';
        } else if (params.has('msclkid')) {
            source = 'bing';
            medium = 'cpc';
        } else if (params.has('fbclid')) {
            source = 'facebook';
            medium = 'paid_social';
        }
    }

    // 2. If STILL no source, check Freenow Marketing Cookie
    if (!source && utmData?.utm_source) {
        source = utmData.utm_source;
        medium = utmData.utm_medium || '';
    }

    // 3. If STILL no source, check Referrer (External only)
    if (!source && referrer && !isInternalReferrer(referrer)) {
        try {
            source = new URL(referrer).hostname;
            medium = 'referral';
        } catch {
            source = 'referral';
            medium = 'referral';
        }
    }

    // 4. Final Fallback: Direct
    if (!source) {
        source = 'direct';
        medium = 'none';
    }

    const currentData: AttributionData = {
        utm_source: source,
        utm_medium: medium,
        utm_campaign: params.get('utm_campaign') || utmData?.utm_campaign || '',
        utm_content: params.get('utm_content') || utmData?.utm_content || '',
        utm_term: params.get('utm_term') || utmData?.utm_term || '',
        landing_page: utmData?.landing_page || window.location.origin + window.location.pathname,
        referrer: utmData?.referrer || referrer,
        timestamp: utmData?.timestamp || new Date().toISOString(),
    };

    // --- PERSISTENCE LOGIC ---

    const existingFt = getAttributionCookie('ft_attr');
    const existingLt = getAttributionCookie('lt_attr');

    // FT: Only write if missing AND not direct
    if (!existingFt && source !== 'direct') {
        setAttributionCookie('ft_attr', currentData);
    }

    // LT: Update unless current is direct and we have a better history
    const isCurrentDirect = source === 'direct';
    const isOldNonDirect = existingLt && existingLt.utm_source !== 'direct';

    if (!(isCurrentDirect && isOldNonDirect)) {
        setAttributionCookie('lt_attr', currentData);
    }
};
